import { useEffect, useRef, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

const DEBOUNCE_MS = 300;

export function usePersistedFormData<T extends FieldValues>(
  key: string | null | undefined,
  form: UseFormReturn<T>,
): number {
  // Track the last key we hydrated for so a per-step consumer re-hydrating
  // on Back navigation (key changes step-1 → step-2 → step-1) gets each
  // step's stored draft. A boolean "restored once" ref would only ever
  // hydrate the first key it saw.
  const lastRestoredKey = useRef<string | null | undefined>(undefined);
  // Hydration counter — bumped on every successful form.reset(parsed) so
  // child effects can distinguish a hydration reset from a user-driven
  // field change on the tick they land together.
  const [hydrationCount, setHydrationCount] = useState(0);

  useEffect(() => {
    if (!key) return;
    if (lastRestoredKey.current === key) return;
    lastRestoredKey.current = key;
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw) as T;
      form.reset(parsed, { keepDirty: false });
      setHydrationCount((c) => c + 1);
    } catch {
      /* ignore */
    }
  }, [key, form]);

  useEffect(() => {
    if (!key) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const subscription = form.watch((values) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          sessionStorage.setItem(key, JSON.stringify(values));
        } catch {
          /* ignore */
        }
      }, DEBOUNCE_MS);
    });
    return () => {
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [key, form]);

  return hydrationCount;
}

export function clearPersistedFormData(key: string | null | undefined): void {
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
